import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { Sparkles, Image as ImageIcon, Code, BookOpen, PenLine, Globe, Video, TrendingUp } from 'lucide-react';

const ICON_MAP = {
  image: ImageIcon,
  code: Code,
  book: BookOpen,
  pen: PenLine,
  globe: Globe,
  video: Video,
  spark: Sparkles,
};

/**
 * Rotating smart-prompt chips.
 * Props:
 *   onPick(prompt) — when the user taps a chip. If omitted, chip tap navigates
 *                    to prompt.route?q=prompt.text (uses the home ask bar pattern).
 *   limit — max chips to show at a time (default 4 on mobile, 6 on desktop)
 *   rotateMs — rotation interval. 0 to disable.
 *   title — small heading above chips, default "Trending prompts"
 */
export default function PromptChips({ onPick, limit, rotateMs = 7000, title = 'Trending prompts' }) {
  const [pool, setPool] = useState([]);
  const [offset, setOffset] = useState(0);
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth >= 640 : false);
  const nav = useNavigate();
  const timerRef = useRef(null);

  // Load pool once
  useEffect(() => {
    api.get('/prompts/trending?limit=30').then(r => setPool(r.data || [])).catch(() => {});
  }, []);

  // Responsive chip count
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Auto-rotate
  useEffect(() => {
    if (!rotateMs || pool.length === 0) return;
    timerRef.current = setInterval(() => {
      setOffset(o => (o + 1) % Math.max(1, pool.length));
    }, rotateMs);
    return () => clearInterval(timerRef.current);
  }, [rotateMs, pool.length]);

  if (pool.length === 0) return null;

  const chipCount = limit || (isDesktop ? 5 : 3);
  const visible = Array.from({ length: Math.min(chipCount, pool.length) }, (_, i) => pool[(offset + i) % pool.length]);

  const pick = (p) => {
    // Fire-and-forget tap analytics
    if (p.id) { api.post(`/prompts/${p.id}/track-click`, { text: p.text }).catch(() => {}); }
    if (onPick) { onPick(p); return; }
    const route = p.route || '/ai/chat';
    nav(`${route}?q=${encodeURIComponent(p.text)}`);
  };

  return (
    <div data-testid="prompt-chips" className="space-y-2">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
        <TrendingUp className="w-3 h-3 text-violet-400" />
        {title}
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap">
        {visible.map((p, i) => {
          const Icon = ICON_MAP[p.icon] || Sparkles;
          return (
            <button
              key={`${offset}-${i}-${p.text}`}
              data-testid={`prompt-chip-${i}`}
              onClick={() => pick(p)}
              className="group flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                         bg-white/5 border border-violet-500/25 hover:border-violet-400/70 hover:bg-violet-500/10
                         transition-all duration-300 text-xs font-medium text-slate-200
                         animate-in fade-in slide-in-from-bottom-1"
              style={{ animationDelay: `${i * 60}ms` }}
              title={p.text}
            >
              <Icon className="w-3.5 h-3.5 text-violet-300 group-hover:text-violet-200" />
              <span className="truncate max-w-[180px] sm:max-w-[240px]">{p.text}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
